import type { JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { Edit3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCreateObject, useObject, useObjectCategory, useUpdateObject } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { SearchCombobox } from '@/shared/components/inputs';
import { TabNav } from '@/shared/components/layout';
import { PageHeader } from '@/shared/components/layout/page-header';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import {
  MetadataBuilder,
  MetadataFormPreview,
} from '@/shared/components/metadata/metadata-builder';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/shared/components/ui';
import {
  createEmptyMetadataSchema,
  createMetadataZodSchema,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';
import { ObjectActivity } from '../components/object-activity';
import { createObjectSchema } from '../schema';
import type { ObjectFormValues } from '../types';

export function ManageObject() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const createObject = useCreateObject();
  const updateObject = useUpdateObject();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const isEditMode = !!id;
  const isCopyMode = !!copyFromId;

  // Use regular or customer view object hooks based on mode
  const { data: object, isLoading: isLoadingObject } = useObject(id || '');
  const { data: objectToCopy } = useObject(copyFromId || '', {
    disabled: !copyFromId,
  });
  const currentObject = object;

  const isGmnAdmin = currentUser?.role === 'gmn-admin';
  const isGmnUser = isGmnAdmin;
  const isCustomer = currentUser?.role === 'customer-admin';

  const treatAsCustomer = isCustomer;

  // Determine if customer can update this object
  let objectCustomerId: string | undefined;
  if (currentObject?.assignedCustomer) {
    objectCustomerId =
      typeof currentObject.assignedCustomer === 'object'
        ? currentObject.assignedCustomer.id
        : currentObject.assignedCustomer;
  }

  const canUpdateObject =
    !isLoadingObject &&
    (isGmnAdmin ||
      (isCustomer &&
        objectCustomerId &&
        objectCustomerId ===
          (typeof currentUser?.customer === 'object'
            ? currentUser?.customer?.id
            : currentUser?.customer)));

  const formDisabled = isLoadingObject || !canUpdateObject;

  // Determine the appropriate back href based on context
  const backHref = isGmnUser ? '/gmn/contract-management/objects' : '/c/my-objects';

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [categoryMetadataZodSchema, setCategoryMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [dynamicMetadataZodSchema, setDynamicMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const form = useForm<ObjectFormValues, any, ObjectFormValues>({
    resolver: zodResolver(
      createObjectSchema(t, categoryMetadataZodSchema, dynamicMetadataZodSchema)
    ),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      assignedCustomer: '',
      dynamicMetadataSchema: createEmptyMetadataSchema(),
      categoryMetadataValues: {},
      dynamicMetadataValues: {},
    },
  });

  // Track selected category ID for fetching its schema
  const selectedCategoryId = form.watch('category') || '';
  const { data: selectedCategory } = useObjectCategory(selectedCategoryId, {
    disabled: !selectedCategoryId,
  });
  const categoryMetadataSchema = selectedCategory?.metadataSchema as JsonSchemaType | undefined;

  useEffect(() => {
    if (isEditMode && object) {
      const assignedCustomerId =
        typeof object.assignedCustomer === 'object'
          ? object.assignedCustomer?.id
          : object.assignedCustomer;

      const categoryId =
        typeof object.category === 'object' ? object.category?.id : object.category;

      form.reset({
        name: object.name,
        description: object.description || '',
        category: categoryId || '',
        assignedCustomer: assignedCustomerId || '',
        dynamicMetadataSchema:
          (object.dynamicMetadataSchema as JsonSchemaType) || createEmptyMetadataSchema(),
        categoryMetadataValues: parseMetadata(
          object.categoryMetadataValues as Record<string, unknown>
        ),
        dynamicMetadataValues: parseMetadata(
          object.dynamicMetadataValues as Record<string, unknown>
        ),
      });
    } else if (isCopyMode && objectToCopy) {
      const assignedCustomerId =
        typeof objectToCopy.assignedCustomer === 'object'
          ? objectToCopy.assignedCustomer?.id
          : objectToCopy.assignedCustomer;

      const categoryId =
        typeof objectToCopy.category === 'object'
          ? objectToCopy.category?.id
          : objectToCopy.category;

      form.reset({
        name: `${objectToCopy.name} (${t('common.copy')})`,
        description: objectToCopy.description || '',
        category: categoryId || '',
        assignedCustomer: assignedCustomerId || '',
        dynamicMetadataSchema:
          (objectToCopy.dynamicMetadataSchema as JsonSchemaType) || createEmptyMetadataSchema(),
        categoryMetadataValues: parseMetadata(
          objectToCopy.categoryMetadataValues as Record<string, unknown>
        ),
        dynamicMetadataValues: parseMetadata(
          objectToCopy.dynamicMetadataValues as Record<string, unknown>
        ),
      });
    }
  }, [isEditMode, isCopyMode, object, objectToCopy, form, t]);

  // Update category metadata zod schema when category changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      setCategoryMetadataZodSchema(createMetadataZodSchema(categoryMetadataSchema, !isCustomer));
    } else {
      setCategoryMetadataZodSchema(z.object({}));
    }
  }, [categoryMetadataSchema, isCustomer]);

  // Update dynamic metadata zod schema when object schema changes
  useEffect(() => {
    const objectSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema && Object.keys(objectSchema.properties || {}).length > 0) {
      setDynamicMetadataZodSchema(createMetadataZodSchema(objectSchema, !isCustomer));
    } else {
      setDynamicMetadataZodSchema(z.object({}));
    }
  }, [form.watch('dynamicMetadataSchema'), isCustomer]);

  // Sanitize category metadata values when category schema changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      const currentValues = (form.getValues('categoryMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, categoryMetadataSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('categoryMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [categoryMetadataSchema, form]);

  // Sanitize dynamic metadata values when object schema changes
  useEffect(() => {
    const objectSchema = form.getValues('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema) {
      const currentValues = (form.getValues('dynamicMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, objectSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('dynamicMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [form.watch('dynamicMetadataSchema'), form]);

  // Initialize drawer schema when opening
  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    if (drawerSchema) {
      form.setValue('dynamicMetadataSchema', drawerSchema);
      setDynamicMetadataZodSchema(createMetadataZodSchema(drawerSchema, !isCustomer));
    }
    setIsMetadataDrawerOpen(false);
  };

  const handleSubmit = async (data: ObjectFormValues) => {
    if (!canUpdateObject) return;

    try {
      if (isEditMode) {
        await updateObject.mutateAsync({ id, data });
        toast.success(t('objects.objectUpdatedSuccessfully'));
      } else {
        const createdObject = await createObject.mutateAsync(data);
        toast.success(t('objects.objectCreatedSuccessfully'));
        navigate(`/gmn/contract-management/objects/${createdObject.doc.id}/details`);
      }
    } catch (_error) {
      toast.error(t('objects.errorSavingObject'));
    }
  };

  const getPageTitle = () => {
    if (!isGmnUser) {
      return isEditMode ? t('objects.objectDetails') : t('objects.newObject');
    }
    return isEditMode
      ? t('objects.editObject')
      : isCopyMode
        ? t('objects.copyObject')
        : t('objects.newObject');
  };

  // Tab navigation logic
  const currentTab = isEditMode ? location.pathname.split('/').pop() || 'details' : 'details';
  const basePath = isGmnUser ? '/gmn/contract-management/objects' : '/c/my-objects';

  const tabs = [
    { value: 'details', label: t('objects.objectDetails') },
    { value: 'activity', label: t('common.activity') },
  ];

  const renderObjectDetailsCard = () => (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>{t('objects.objectDetails')}</CardTitle>
        <CardDescription>{t('objects.objectDetailsDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('common.name')}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={formDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.description')}</FormLabel>
                <FormControl>
                  <Textarea {...field} disabled={formDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Category selector - only for GMN users */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('objectCategories.category')}</FormLabel>
                  <FormControl>
                    <SearchCombobox
                      value={field.value || ''}
                      onSelect={field.onChange}
                      collectionKey="object-categories"
                      searchKey="name"
                      clearValue=""
                      disabled={formDisabled || !isGmnAdmin}
                      texts={{
                        selectItemMsg: t('objectCategories.selectCategory'),
                        searchPlaceholder: t('objectCategories.selectCategory'),
                        noResultsMsg: t('objectCategories.noCategory'),
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Only show assigned customer field for GMN users not in customer view mode */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="assignedCustomer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers.assignedCustomer')}</FormLabel>
                  <FormControl>
                    <SearchCombobox
                      value={field.value}
                      onSelect={field.onChange}
                      collectionKey="customers"
                      searchKey="name"
                      clearValue=""
                      disabled={formDisabled || !isGmnAdmin}
                      texts={{
                        selectItemMsg: t('customers.selectCustomer'),
                        searchPlaceholder: t('customers.searchCustomers'),
                        noResultsMsg: t('customers.noCustomersFound'),
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );

  const hasCategoryMetadata =
    categoryMetadataSchema && Object.keys(categoryMetadataSchema.properties || {}).length > 0;

  const renderCategoryMetadataCard = () => {
    if (!selectedCategoryId || !hasCategoryMetadata) return null;

    return (
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>
            {t('objectCategories.categoryMetadata')}: {selectedCategory?.name}
          </CardTitle>
          <CardDescription>{t('objectCategories.categoryMetadataDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <MetadataAutoForm
            schema={categoryMetadataZodSchema}
            form={form}
            path={['categoryMetadataValues']}
            metadataSchema={categoryMetadataSchema!}
            isCustomer={treatAsCustomer}
            disabled={formDisabled}
          />
        </CardContent>
      </Card>
    );
  };

  const renderCustomMetadataCard = () => (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="space-y-1">
          <CardTitle>{t('metadata.customMetadata')}</CardTitle>
          <CardDescription>{t('metadata.customMetadataBuilderDescription')}</CardDescription>
        </div>
        {isGmnAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={openMetadataDrawer}
            type="button"
          >
            <Edit3 className="size-3!" />
            {t('metadata.addCustomMetadata')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {form.watch('dynamicMetadataSchema') &&
        Object.keys(form.watch('dynamicMetadataSchema.properties') || {}).length > 0 ? (
          <MetadataAutoForm
            schema={dynamicMetadataZodSchema}
            form={form}
            path={['dynamicMetadataValues']}
            metadataSchema={form.watch('dynamicMetadataSchema') as JsonSchemaType}
            isCustomer={treatAsCustomer}
            disabled={formDisabled}
          />
        ) : (
          <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
        )}
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (currentTab) {
      case 'details':
        return (
          <div className="grid gap-6">
            <Form {...form}>
              <div className="grid gap-6 md:grid-cols-2">
                {renderObjectDetailsCard()}
                {renderCategoryMetadataCard()}
                {renderCustomMetadataCard()}
              </div>
            </Form>
          </div>
        );
      case 'activity':
        return <ObjectActivity objectId={id} />;
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        title={getPageTitle()}
        backHref={backHref}
        actions={
          <div className="flex items-center gap-2">
            {currentTab === 'details' && (
              <>
                <Button variant="outline" onClick={() => navigate(backHref)}>
                  {t('common.discard')}
                </Button>
                {canUpdateObject && (
                  <Button
                    form="object-form"
                    type="submit"
                    disabled={
                      (isEditMode ? updateObject.isPending : createObject.isPending) ||
                      !canUpdateObject
                    }
                  >
                    {(isEditMode ? updateObject.isPending : createObject.isPending)
                      ? isEditMode
                        ? t('common.updating')
                        : t('common.creating')
                      : isEditMode
                        ? t('common.update')
                        : t('common.create')}
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {/* Only show tabs when editing an existing object */}
      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} rightContent={null} />}

      <form id="object-form" onSubmit={form.handleSubmit(handleSubmit)}>
        {/* Show content based on tab or show all content for creation */}
        {isEditMode ? renderContent() : renderContent()}
      </form>

      {isGmnAdmin && (
        <Dialog open={isMetadataDrawerOpen} onOpenChange={setIsMetadataDrawerOpen}>
          <DialogContent
            className="!max-w-[90vw] !w-[1400px] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
            showCloseButton={false}
          >
            <div className="flex-1 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-[70vh]">
                <MetadataBuilder
                  value={drawerSchema}
                  onChange={setDrawerSchema}
                  className="border-0 rounded-none shadow-none lg:border-r h-full"
                  header={false}
                />
                <MetadataFormPreview
                  schema={drawerSchema}
                  className="border-0 rounded-none shadow-none hidden lg:flex h-full"
                  header={false}
                />
              </div>
            </div>
            <DialogFooter className="border-t p-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMetadataDrawerOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" onClick={handleMetadataBuilderSave}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
